import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Briefcase, GraduationCap, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { errMsg, useApi } from "@/lib/api";
import { homePath, useMe } from "@/context/MeContext";

const OPTIONS = [
  { role: "learner", icon: GraduationCap, title: "I want to learn", body: "Build job-ready skills, track progress and create a verified skills profile." },
  { role: "employer", icon: Briefcase, title: "I'm hiring", body: "Set up your company and, once approved, discover candidates with verified skills." },
];

export default function Welcome() {
  const api = useApi();
  const { refresh } = useMe();
  const navigate = useNavigate();
  const [role, setRole] = useState("learner");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    try {
      await api.post("/me/role", { role });
      const me = await refresh();
      navigate(homePath(me), { replace: true });
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="grid-paper flex min-h-screen flex-col items-center justify-center p-4">
      <div className="w-full max-w-2xl rounded-2xl border border-slate-200 bg-white p-6 shadow-sm sm:p-10">
        <Logo />
        <h1 className="mt-8 text-3xl font-extrabold tracking-tight text-slate-900">How will you use Crowlearn?</h1>
        <p className="mt-2 text-sm text-slate-600">Each account has one role. Choose carefully — an admin can change it later if needed.</p>
        <div className="mt-8 grid gap-4 sm:grid-cols-2" role="radiogroup">
          {OPTIONS.map(({ role: r, icon: Icon, title, body }) => (
            <button
              key={r} type="button" role="radio" aria-checked={role === r} onClick={() => setRole(r)}
              data-testid={`role-option-${r}`}
              className={`rounded-xl border-2 p-5 text-left transition-colors ${role === r ? "border-[#222d99] bg-[#e8eaf6]" : "border-slate-200 hover:border-slate-400"}`}
            >
              <Icon className="h-6 w-6 text-[#222d99]" />
              <p className="mt-4 font-display text-lg font-bold text-slate-900">{title}</p>
              <p className="mt-1 text-sm text-slate-600">{body}</p>
            </button>
          ))}
        </div>
        <Button onClick={submit} disabled={busy} className="mt-8 w-full rounded-full bg-[#222d99] hover:bg-[#1a237e]" size="lg" data-testid="role-continue-btn">
          {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Continue
        </Button>
      </div>
    </div>
  );
}
